import { motion } from "framer-motion";
import { BookOpen, ExternalLink } from "lucide-react";

const books = [
    {
        id: 1,
        num: "I",
        title: "Node Zero",
        subtitle: "Spectra // Blackline · Book I",
        author: "Elias Rane",
        cover:
            "https://customer-assets.emergentagent.com/job_rane-dynamics-films/artifacts/71s45106_file_00000000531472308d5bcc0f51861252.png",
        status: "Available on KDP",
        available: true,
        url: "https://www.amazon.com/dp/B0G4HWSK1D",
        blurb:
            "The trilogy opens at the origin. A single node comes online, a signal breaks containment, and the future the machine tried to bury starts writing itself back into the world.",
    },
    {
        id: 2,
        num: "II",
        title: "Tear Cascade",
        subtitle: "Spectra // Blackline · Book II",
        author: "Elias Rane",
        cover:
            "https://customer-assets.emergentagent.com/job_rane-dynamics-films/artifacts/gcopofd8_file_00000000785471f892194fa3209959de.png",
        status: "Available on KDP",
        available: true,
        url: "https://www.amazon.com/dp/B0G4J3WJ5R",
        blurb:
            "Two futures now run parallel and the storm between them is real. Sovereign Nodes come online across the continent as the old machine tightens its grip — and the Blackline suit finds its wearer.",
    },
    {
        id: 3,
        num: "III",
        title: "Project U",
        subtitle: "Spectra // Blackline · Book III",
        author: "Elias Rane",
        cover:
            "https://customer-assets.emergentagent.com/job_rane-dynamics-films/artifacts/ba8ne1jp_file_00000000889c71fdb1d0201051bc2fb3.png",
        status: "Available Now",
        available: true,
        url: null,
        links: [
            { label: "Kindle", url: "https://www.amazon.com/dp/B0HK1ZRHDC" },
            { label: "Paperback", url: "https://www.amazon.com/dp/B0HK7G74RB" },
        ],
        blurb:
            "The trilogy closes with Project U. Every thread, every character, every impossibility folds into a single, unified signal — and one final choice.",
    },
];

export default function Trilogy() {
    return (
        <section
            id="trilogy"
            data-testid="trilogy-section"
            className="relative bg-[#09090B] text-white py-24 md:py-40 grain overflow-hidden"
        >
            {/* ambient orange glow */}
            <div className="absolute top-1/3 -left-40 w-[500px] h-[500px] rounded-full bg-[#FF5500] opacity-10 blur-[120px] pointer-events-none" />
            <div className="absolute bottom-0 -right-40 w-[600px] h-[600px] rounded-full bg-[#3B82F6] opacity-10 blur-[140px] pointer-events-none" />

            <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-10">
                <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-16 md:mb-24">
                    <div>
                        <span className="text-xs font-bold uppercase tracking-[0.25em] text-[#FF5500]">
                            002 // The Trilogy
                        </span>
                        <h2 className="mt-4 font-display font-black text-5xl md:text-7xl leading-[0.9] tracking-tight max-w-3xl">
                            The Spectra <span className="italic font-light text-zinc-400">//</span> Blackline
                            <br />
                            Universe.
                        </h2>
                    </div>
                    <p className="text-lg text-zinc-400 max-w-md">
                        Three books. One continuous signal. The literary blueprint for
                        everything we&apos;re engineering into existence.
                    </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-12">
                    {books.map((b, i) => (
                        <motion.article
                            key={b.id}
                            initial={{ opacity: 0, y: 40 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-80px" }}
                            transition={{ duration: 0.7, delay: i * 0.12 }}
                            data-testid={`book-card-${b.id}`}
                            className="book-card relative rounded-2xl overflow-hidden bg-white/[0.03] backdrop-blur-xl border border-white/10 p-6 md:p-8 flex flex-col"
                        >
                            <div className="flex items-start justify-between mb-6">
                                <span className="font-display font-black text-4xl text-white/20">
                                    {b.num}
                                </span>
                                <span
                                    className={`text-[10px] font-bold uppercase tracking-[0.2em] px-3 py-1.5 rounded-full ${
                                        b.available
                                            ? "bg-[#FF5500] text-white"
                                            : "bg-white/10 text-zinc-300 border border-white/10"
                                    }`}
                                >
                                    {b.status}
                                </span>
                            </div>

                            <div className="aspect-[2/3] rounded-xl overflow-hidden bg-zinc-900 mb-6 relative">
                                <img
                                    src={b.cover}
                                    alt={`${b.title} — Spectra // Blackline Book ${b.num} by ${b.author}`}
                                    className="w-full h-full object-cover"
                                />
                            </div>

                            <h3 className="font-display font-bold text-2xl text-white">
                                {b.title}
                                <span className="text-[#FF5500]">.</span>
                            </h3>
                            <div className="text-sm text-zinc-400 mb-4">
                                {b.subtitle} · <span className="text-zinc-500">by {b.author}</span>
                            </div>
                            <p className="text-zinc-300 leading-relaxed text-sm flex-1">{b.blurb}</p>

                            <div className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-3">
                                {b.links ? (
                                    b.links.map((l) => (
                                        <a
                                            key={l.label}
                                            href={l.url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            data-testid={`book-cta-${b.id}-${l.label.toLowerCase()}`}
                                            className="inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-wider text-[#FF5500] hover:text-white transition-colors"
                                        >
                                            <BookOpen size={14} />
                                            {l.label}
                                            <ExternalLink size={12} />
                                        </a>
                                    ))
                                ) : (
                                    <a
                                        href={b.available ? b.url : "#contact"}
                                        target={b.available ? "_blank" : undefined}
                                        rel={b.available ? "noopener noreferrer" : undefined}
                                        data-testid={`book-cta-${b.id}`}
                                        className={`inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-wider transition-colors ${
                                            b.available
                                                ? "text-[#FF5500] hover:text-white"
                                                : "text-zinc-500"
                                        }`}
                                    >
                                        {b.available ? (
                                            <>
                                                <BookOpen size={14} />
                                                View on Amazon
                                                <ExternalLink size={12} />
                                            </>
                                        ) : (
                                            <>
                                                <BookOpen size={14} />
                                                Notify me when released
                                            </>
                                        )}
                                    </a>
                                )}
                            </div>
                        </motion.article>
                    ))}
                </div>

                <div className="mt-16 md:mt-24 rounded-2xl border border-white/10 bg-white/[0.02] backdrop-blur-xl p-8 md:p-12 flex flex-col md:flex-row md:items-center gap-6 md:gap-10">
                    <div className="flex-1">
                        <div className="text-xs font-bold uppercase tracking-[0.25em] text-[#FF5500] mb-3">
                            Written by Elias Rane
                        </div>
                        <p className="text-zinc-300 text-base md:text-lg max-w-2xl">
                            <span className="text-white font-semibold">Node Zero</span>,{" "}
                            <span className="text-white font-semibold">Tear Cascade</span>, and the
                            trilogy&apos;s closing volume —{" "}
                            <span className="text-white font-semibold">Project U</span> — are all
                            live on Amazon now.
                        </p>
                    </div>
                    <a
                        href="https://www.amazon.com/s?k=Elias+Rane"
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="trilogy-cta"
                        className="inline-flex items-center gap-2 bg-white text-zinc-950 hover:bg-[#FF5500] hover:text-white font-semibold text-sm uppercase tracking-wider px-6 py-3.5 rounded-full transition-all duration-300"
                    >
                        Shop on Amazon
                        <ExternalLink size={14} />
                    </a>
                </div>
            </div>
        </section>
    );
}
